import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { ApiError, isNotAvailable } from '@/api/client'
import { meQuery } from '@/api/queries'
import { FilterBar } from '@/components/FilterBar'
import { EmptyState, PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  deadlinesInfiniteQuery,
  toAmpel,
  type DeadlineFilters,
  type DeadlineItem,
} from '@/features/deadlines/api'
import { kindLabel } from '@/features/deadlines/labels'
import { AmpelBadge } from '@/features/orders/AmpelBadge'
import { requireNav } from '@/nav/guard'

const text = z.union([z.string(), z.number()]).transform(String).optional().catch(undefined)
const STATES = ['OPEN', 'EXTENDED', 'OVERDUE', 'EXPIRED', 'DONE', 'CANCELLED'] as const
const AMPELN = ['RED', 'AMBER', 'GREEN', 'DONE'] as const
/** Fristarten, die das Backend heute kennt; weitere Arten ergänzt die Liste der geladenen Fristen. */
const KNOWN_TYPES = [
  'ACCEPTANCE',
  'VAO',
  'PERMIT_VALIDITY',
  'CLIENT_DEADLINE',
  'CLIENT_SERVICE_TIME',
]

export const Route = createFileRoute('/_app/deadlines')({
  beforeLoad: requireNav('deadlines'),
  validateSearch: z.object({
    kind: text,
    responsible: text,
    area: text,
    ampel: z.enum(AMPELN).optional().catch(undefined),
    state: z.enum(STATES).optional().catch(undefined),
  }),
  component: DeadlinesPage,
})

function DeadlinesPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: me } = useQuery(meQuery)

  const update = useCallback(
    (patch: Partial<typeof search>) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  )

  // Alle Filter gehen an den Server (Rechte und Sichtbarkeit ebenfalls)
  const filters: DeadlineFilters = {
    type: search.kind,
    responsibleUserId: search.responsible,
    rajon: search.area,
    ampel: search.ampel,
    status: search.state,
  }
  const query = useInfiniteQuery(deadlinesInfiniteQuery(filters))
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data])

  const all = { value: '', label: t('common.all') }
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )
  const responsibleLabel = (id: string | null | undefined, name?: string | null) =>
    !id
      ? t('common.none')
      : id === me?.userId
        ? t('deadlines.me')
        : (name ?? t('deadlines.userShort', { id: id.slice(0, 8) }))

  const typeOptions = [
    ...new Set([...KNOWN_TYPES, ...rows.map((r) => r.typeCode), search.kind ?? '']),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))
  const people = new Map<string, string | null | undefined>(
    rows.flatMap((r) =>
      r.responsibleUserId ? [[r.responsibleUserId, r.responsibleName] as const] : [],
    ),
  )
  if (search.responsible && !people.has(search.responsible))
    people.set(search.responsible, undefined)
  const areas = [...new Set([...rows.map((r) => r.order?.rajon ?? ''), search.area ?? ''])]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b))

  return (
    <>
      <PageHeader
        title={t('pages.deadlines.title')}
        description={t('pages.deadlines.description')}
      />
      <FilterBar
        filters={[
          {
            id: 'kind',
            kind: 'select',
            label: t('deadlines.filterType'),
            value: search.kind ?? '',
            options: [all, ...typeOptions.map((k) => ({ value: k, label: kindLabel(t, k) }))],
            onChange: (v) => update({ kind: v || undefined }),
          },
          {
            id: 'responsible',
            kind: 'select',
            label: t('deadlines.filterResponsible'),
            value: search.responsible ?? '',
            options: [
              all,
              ...[...people].map(([id, name]) => ({
                value: id,
                label: responsibleLabel(id, name),
              })),
            ],
            onChange: (v) => update({ responsible: v || undefined }),
          },
          {
            id: 'area',
            kind: 'select',
            label: t('deadlines.filterArea'),
            value: search.area ?? '',
            options: [all, ...areas.map((a) => ({ value: a, label: a }))],
            onChange: (v) => update({ area: v || undefined }),
          },
          {
            id: 'ampel',
            kind: 'select',
            label: t('deadlines.filterAmpel'),
            value: search.ampel ?? '',
            options: [all, ...AMPELN.map((v) => ({ value: v, label: t(`ampel.${toAmpel(v)}`) }))],
            onChange: (v) => update({ ampel: (v || undefined) as typeof search.ampel }),
          },
          {
            id: 'state',
            kind: 'select',
            label: t('deadlines.filterState'),
            value: search.state ?? '',
            options: [
              all,
              ...STATES.map((v) => ({ value: v, label: t(`order.deadlineState.${v}`) })),
            ],
            onChange: (v) => update({ state: (v || undefined) as typeof search.state }),
          },
        ]}
      />

      {query.isPending ? (
        <Skeleton className="h-32 w-full" aria-label={t('common.loading')} />
      ) : query.isError ? (
        query.error instanceof ApiError && query.error.status === 403 ? (
          // Fristen sieht nur, wer deadline.read hat (der Auftraggeber nicht)
          <EmptyState title={t('forbidden.title')} body={t('forbidden.body')} />
        ) : isNotAvailable(query.error) ? (
          <EmptyState title={t('errors.notConnectedTitle')} body={t('errors.notConnectedBody')} />
        ) : (
          <EmptyState title={t('deadlines.loadError')} body={t('errors.network')}>
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          </EmptyState>
        )
      ) : rows.length === 0 ? (
        <EmptyState title={t('deadlines.emptyTitle')} body={t('deadlines.emptyBody')} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border bg-card">
            <table className="w-full text-left" aria-label={t('deadlines.listLabel')}>
              <thead className="bg-muted text-sm">
                <tr>
                  {(
                    [
                      'colAmpel',
                      'colType',
                      'colOrder',
                      'colArea',
                      'colActionDue',
                      'colDue',
                      'colResponsible',
                      'colState',
                      'colAck',
                      'colEscalation',
                    ] as const
                  ).map((key) => (
                    <th key={key} scope="col" className="px-4 py-3">
                      {t(`deadlines.${key}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <DeadlineTableRow
                    key={row.id}
                    row={row}
                    formatDate={formatDate}
                    responsibleLabel={responsibleLabel}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {query.hasNextPage && (
            <div className="mt-4">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? t('common.loading') : t('orders.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </>
  )
}

function DeadlineTableRow({
  row,
  formatDate,
  responsibleLabel,
}: {
  row: DeadlineItem
  formatDate: (iso: string) => string
  responsibleLabel: (id: string | null | undefined, name?: string | null) => string
}) {
  const { t } = useTranslation()
  const { ack, escalation } = row
  return (
    <tr className="border-t align-top" data-deadline={row.id}>
      <td className="px-4 py-2">
        <AmpelBadge ampel={toAmpel(row.ampel)} />
      </td>
      <td className="px-4 py-2">{kindLabel(t, row.typeCode)}</td>
      <td className="px-4 py-2">
        {row.order ? (
          <Link
            to="/orders/$orderId"
            params={{ orderId: row.order.id }}
            className="inline-flex min-h-11 items-center font-medium whitespace-nowrap text-primary underline"
          >
            {row.order.number}
          </Link>
        ) : (
          t('common.none')
        )}
      </td>
      <td className="px-4 py-2">{row.order?.rajon ?? t('common.none')}</td>
      <td className="px-4 py-2">{formatDate(row.actionDueAt)}</td>
      <td className="px-4 py-2">{formatDate(row.dueAt)}</td>
      <td className="px-4 py-2">{responsibleLabel(row.responsibleUserId, row.responsibleName)}</td>
      <td className="px-4 py-2">
        <Badge variant="secondary">{t(`order.deadlineState.${row.status}`)}</Badge>
      </td>
      <td className="px-4 py-2">
        {ack.state === 'NONE'
          ? t('deadlines.ackNone')
          : ack.state === 'ACKNOWLEDGED'
            ? t('deadlines.ackDone', {
                date: ack.acknowledgedAt ? formatDate(ack.acknowledgedAt) : t('common.none'),
              })
            : t('deadlines.ackOpen', {
                date: ack.ackDueAt ? formatDate(ack.ackDueAt) : t('common.none'),
              })}
      </td>
      <td className="px-4 py-2">
        {escalation.level > 0
          ? t('deadlines.escalationLevel', { count: escalation.level })
          : t('deadlines.escalationNone')}
      </td>
    </tr>
  )
}
